import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

/**
 * TimePicker Component - TYPED INPUT ONLY
 * 
 * GLOBAL UX RULE (Authoritative):
 * - Typed input only, no dropdown picker
 * - Accept: HH, HH.MM, HH:MM, H:MM, HHMM
 * - Auto-normalise to HH:MM on blur
 * - Store as HH:MM
 * - No browser-native time pickers
 * - No dark-on-dark text
 */

// Parse various time formats to HH:MM
const parseTimeInput = (input) => {
  if (!input) return null;
  
  const cleaned = input.trim().toUpperCase();
  
  // Try HH:MM format (e.g., "08:30", "8:30")
  let match = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }
  
  // Try HH.MM format (dot separator, e.g., "08.30", "8.30")
  match = cleaned.match(/^(\d{1,2})\.(\d{2})$/);
  if (match) {
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }
  
  // Try H:MM format (single digit hour, e.g., "8:30")
  match = cleaned.match(/^(\d):(\d{2})$/);
  if (match) {
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h >= 0 && h <= 9 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }
  
  // Try HHMM format (no separator, e.g., "0830", "1730")
  match = cleaned.match(/^(\d{2})(\d{2})$/);
  if (match) {
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }
  
  // Try HH only (assume :00 minutes, e.g., "8" → "08:00", "17" → "17:00")
  match = cleaned.match(/^(\d{1,2})$/);
  if (match) {
    const h = parseInt(match[1], 10);
    if (h >= 0 && h <= 23) {
      return `${h.toString().padStart(2, "0")}:00`;
    }
  }
  
  // Try HH:MM AM/PM format (e.g., "8:30 AM", "5:30 PM")
  match = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (match) {
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const ampm = match[3];
    
    if (h >= 1 && h <= 12 && m >= 0 && m <= 59) {
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }
  
  return null;
};

export function TimePicker({ 
  value, 
  onChange, 
  placeholder = "HH:MM",
  className,
  disabled = false,
  ...props 
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [error, setError] = React.useState("");

  // Sync input value with prop value
  React.useEffect(() => {
    if (value) {
      setInputValue(value);
      setError("");
    } else {
      setInputValue("");
    }
  }, [value]);

  // Handle direct text input
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError(""); // Clear error while typing
  };

  // Validate and normalise on blur
  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      onChange("");
      setError("");
      return;
    }

    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      onChange(parsed);
      setInputValue(parsed);
      setError("");
    } else {
      setError("Invalid time");
    }
  };

  // Handle keyboard submit
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputBlur();
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          error && "border-red-500",
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}

export default TimePicker;
